import http from "../http";
import {
  PasswordStrength,
  User,
  UserData
} from "../../../domain/entities/entities";
import { AuthenticationRepository } from "../repository";
import { wrapErrorHandler } from "../utils/wrap_error_handler";

class _AuthenticationRepository implements AuthenticationRepository {
  async login(user: User): Promise<boolean> {
    return wrapErrorHandler<boolean>(async () => {
      const credentials = {
        login: user.login,
        password: user.password
      };

      const response = await http.post("/api/login", credentials);
      return response.data.success;
    });
  }

  async finishOnBoarding(user: User): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.post("/api/decola/user/onBoarding/finish", user);
    });
  }

  async register(user: User): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      const response = await http.post("api/register", { user: user });
      return response.data;
    });
  }

  async logout(): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.post("api/decola/logout");
      localStorage.removeItem("user");
    });
  }

  async getIsAdmin(): Promise<boolean> {
    return wrapErrorHandler<boolean>(async () => {
      const response = await http.get("/api/decola/user/isAdmin");
      return response.data.isAdmin;
    });
  }

  async getIsManager(): Promise<boolean> {
    return wrapErrorHandler<boolean>(async () => {
      const response = await http.get("/api/decola/user/isManager");
      return response.data.isManager;
    });
  }

  async checkUserCredentials(userData: UserData): Promise<any> {
    return wrapErrorHandler<any>(async () => {
      const response = await http.post(
        "/api/passwordRecovery/check/data",
        userData
      );
      return response.data;
    });
  }

  async recoverPassword(
    login: string
  ): Promise<{ recoveryType: string; document: string }> {
    return wrapErrorHandler<{ recoveryType: string; document: string }>(
      async () => {
        const response = await http.post("/api/passwordRecovery/by/document", {
          document: login
        });
        return response.data;
      }
    );
  }

  async recoverPasswordByEmail(
    login: string
  ): Promise<{ recoveryType: string; document: string }> {
    return wrapErrorHandler<{ recoveryType: string; document: string }>(
      async () => {
        const response = await http.post("/api/passwordRecovery/by/email", {
          email: login
        });
        return response.data;
      }
    );
  }

  async changePassword(password: string, confirmation: string): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      return await http.post("/api/user/change/password", {
        password,
        confirmation
      });
    });
  }

  async resetPasswordByEmail(
    password: string,
    confirmation: string,
    token: string
  ): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      return await http.post(
        "/api/resetPassword/by/email",
        { password, confirmation, token },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
    });
  }

  async resetPasswordByData(
    password: string,
    confirmation: string,
    token: string
  ): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      return await http.post(
        "/api/resetPassword/by/data",
        { password, confirmation, token },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
    });
  }

  async validatePasswordStrength(password: string): Promise<PasswordStrength> {
    return wrapErrorHandler<PasswordStrength>(async () => {
      const response = await http.post("/api/decola/get/password/security", {
        password: password
      });
      return response.data;
    });
  }

  async acceptTerm(): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      return await http.post("/api/user/accept/term");
    });
  }
}

export const authenticationRepository: AuthenticationRepository = new _AuthenticationRepository();
